import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useNavigate, useLocation } from 'react-router-dom';
import { functions } from '../firebase-config';
import { SplitScreenCourse } from '../components/course';
import { useAuth } from '../context/AuthContext';
import NeuralNexusBackground from '../components/shared/NeuralNexusBackground';
import { DESIGN_SYSTEM } from '../theme/designSystem';

// Thin adapter wrappers around existing Firebase callables.
// Chat → operatorChat (same callable the live /chat route uses)
// Sandbox → callAI (simple one-shot callable)

function useChatAdapter(currentUser) {
  const sessionIdRef = useRef(crypto.randomUUID());
  const messagesRef = useRef([]);

  return useMemo(() => ({
    async send({ message, currentLevel, sandboxHistory, freshStart = false }) {
      const operatorChat = httpsCallable(functions, 'operatorChat');

      if (!freshStart) {
        // Append new user message to running history
        messagesRef.current = [
          ...messagesRef.current,
          { role: 'user', content: message },
        ];
      }

      const result = await operatorChat({
        sessionId: sessionIdRef.current,
        messages: freshStart ? [] : messagesRef.current,
        freshStart,
      });

      const reply = result?.data?.reply || '';
      const openSplitScreen = Boolean(result?.data?.openSplitScreen);
      const closeSplitScreen = Boolean(result?.data?.closeSplitScreen);
      const sandboxAction = result?.data?.sandboxAction || null;
      const sandboxPhase = result?.data?.sandboxPhase || null;
      const sandboxPrefill = result?.data?.sandboxPrefill || '';

      // Append assistant reply so history stays consistent
      if (reply) {
        messagesRef.current = [
          ...messagesRef.current,
          { role: 'assistant', content: reply },
        ];
      }

      return {
        response: reply,
        openSplitScreen,
        closeSplitScreen,
        sandboxAction,
        sandboxPhase,
        prefillSandbox: sandboxPrefill,
        autoRun: sandboxPhase === 'see',
      };
    },

    async onSandboxEvent(entry) {
      // Optional: could forward sandbox context to the chat function later
    },
  }), []);
}

function useSandboxAdapter() {
  return useMemo(() => ({
    async run({ prompt, model }) {
      const callAI = httpsCallable(functions, 'callAI');
      const result = await callAI({ userMessage: prompt, model });
      return result?.data?.text || result?.data?.response || result?.data?.reply || 'No response.';
    },
  }), []);
}

export default function V2CoursePage() {
  const { currentUser, isAdmin, userProfile, loading } = useAuth();
  const hasPaidTier = isAdmin || !!userProfile?.tier;
  const navigate = useNavigate();
  const location = useLocation();
  const { fromBot = false, phase = null, prefill = '', autoRun = false, returnPath = '/dashboard' } = location.state || {};
  const chatAdapter = useChatAdapter(currentUser);
  const sandboxAdapter = useSandboxAdapter();

  useEffect(() => {
    if (loading) return;
    if (!hasPaidTier) { navigate('/pricing', { replace: true }); return; }
  }, [loading, hasPaidTier, navigate]);

  const handleExitSplitScreen = useCallback(() => {
    if (returnPath) {
      navigate(returnPath, { replace: true });
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/dashboard', { replace: true });
  }, [navigate, returnPath]);

  if (loading || !hasPaidTier) {
    return null;
  }

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', background: DESIGN_SYSTEM.backgrounds.course, paddingTop: `${DESIGN_SYSTEM.layout.globalNavHeight}px`, boxSizing: 'border-box' }}>
      <NeuralNexusBackground />
      <SplitScreenCourse
        initialLevel={1}
        chatAdapter={chatAdapter}
        sandboxAdapter={sandboxAdapter}
        onGraduate={(level) => console.log('[V2CoursePage] Student graduated to level', level)}
        onExit={handleExitSplitScreen}
        initialPhase={phase}
        initialPrefill={prefill}
        autoRun={autoRun}
        autoStart={!fromBot}
        fromBot={fromBot}
        returnPath={returnPath}
      />
    </div>
  );
}
